import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    showNumbers?: boolean;
    maxVisiblePages?: number;
}

export default function Pagination({
    currentPage,
    totalPages,
    onPageChange,
    showNumbers = true,
    maxVisiblePages = 5
}: PaginationProps) {
    const { t } = useTranslation();
    const effectiveTotalPages = Math.max(1, totalPages);
    const effectiveCurrentPage = Math.min(Math.max(1, currentPage), effectiveTotalPages);

    const getVisiblePages = () => {
        const pages = [];
        const half = Math.floor(maxVisiblePages / 2);
        let start = Math.max(1, effectiveCurrentPage - half);
        let end = Math.min(effectiveTotalPages, start + maxVisiblePages - 1);

        if (end - start + 1 < maxVisiblePages) {
            start = Math.max(1, end - maxVisiblePages + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return pages;
    };

    const visiblePages = getVisiblePages();

    return (
        <div className="flex items-center justify-center space-x-2">
            <button
                onClick={() => onPageChange(effectiveCurrentPage - 1)}
                disabled={effectiveCurrentPage === 1}
                className="flex items-center px-3 py-2 text-sm font-medium text-muted-foreground border border-muted-foreground rounded-lg hover:opacity-60 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <ChevronLeft className="w-4 h-4 md:mr-1" />
                <span className="hidden md:inline">{t('pagination.previous')}</span>
            </button>

            {showNumbers && (
                <div className="hidden sm:flex space-x-1">
                    {visiblePages[0] > 1 && (
                        <>
                            <button
                                onClick={() => onPageChange(1)}
                                className="px-3 py-2 text-sm font-medium text-muted-foreground border border-muted-foreground rounded-lg hover:opacity-60"
                            >
                                1
                            </button>
                            {visiblePages[0] > 2 && (
                                <span className="px-3 py-2 text-sm font-medium text-muted-foreground">...</span>
                            )}
                        </>
                    )}

                    {visiblePages.map((page) => (
                        <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg hover:opacity-60 border ${page === effectiveCurrentPage
                                ? 'bg-primary dark:bg-transparent text-white dark:text-primary border-primary'
                                : 'text-muted-foreground border-muted-foreground'
                                }`}
                        >
                            {page}
                        </button>
                    ))}

                    {visiblePages[visiblePages.length - 1] < effectiveTotalPages && (
                        <>
                            {visiblePages[visiblePages.length - 1] < effectiveTotalPages - 1 && (
                                <span className="px-3 py-2 text-sm font-medium text-muted-foreground">...</span>
                            )}
                            <button
                                onClick={() => onPageChange(effectiveTotalPages)}
                                className="px-3 py-2 text-sm font-medium text-muted-foreground border border-muted-foreground rounded-lg hover:opacity-60"
                            >
                                {effectiveTotalPages}
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Mostrar página actual en pantallas pequeñas */}
            <div className="sm:hidden flex items-center space-x-2">
                <span className="text-xs font-medium text-muted-foreground rounded-lg px-2 py-1">
                    {effectiveCurrentPage} / {effectiveTotalPages}
                </span>
            </div>

            <button
                onClick={() => onPageChange(effectiveCurrentPage + 1)}
                disabled={effectiveCurrentPage === effectiveTotalPages}
                className="flex items-center px-3 py-2 text-sm font-medium text-muted-foreground border border-muted-foreground rounded-lg hover:opacity-60 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <span className="hidden md:inline">{t('pagination.next')}</span>
                <ChevronRight className="w-4 h-4 md:ml-1" />
            </button>
        </div>
    );
} 